import { useState } from 'react';
import { SimulationConfig, CustomMelody } from '../types';
import { AUDIO } from '../services/AudioService';
import { CloudService } from '../services/CloudService';

export const useFileHandlers = (
  config: SimulationConfig,
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>
) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [isUploadingBallSound, setIsUploadingBallSound] = useState(false);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    setIsAnalyzing(true); setAnalysisProgress(0);
    try {
      const frequencies = await AUDIO.analyzeFile(file, config.audioCropStart, config.audioCropEnd, (p: number) => setAnalysisProgress(p));
      const uploadedUrl = await CloudService.uploadFile(file);
      const soundId = Math.random().toString(36).substring(7);
      const soundName = file.name.replace(/\.[^/.]+$/, "");
      const newMelody: CustomMelody = {
          id: soundId,
          name: soundName,
          frequencies: frequencies,
          format: 'FREQUENCIES',
          url: uploadedUrl || undefined
      };
      
      setConfig((prev: SimulationConfig) => ({ 
          ...prev, 
          customMelodies: [...prev.customMelodies, newMelody],
          selectedMelody: soundId 
      }));
      
      // Save to cloud melodies table
      await CloudService.registerMelody(newMelody.name, newMelody);
      
      // Save to cloud songs table (audio)
      if (uploadedUrl) {
          await CloudService.registerSong(newMelody.name, uploadedUrl);
      }
      
      // Also save to converted_songs for backward compatibility or specific use case
      const reader = new FileReader();
      reader.onload = async (evt) => {
          const dataUrl = evt.target?.result as string;
          if (dataUrl) {
              await CloudService.registerConvertedSong(file.name.replace(/\.[^/.]+$/, ""), dataUrl);
          }
      };
      reader.readAsDataURL(file);
    } catch (e) { alert("Failed to analyze audio."); }
    setIsAnalyzing(false); setAnalysisProgress(0);
  };

  const handleBallSoundUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    setIsUploadingBallSound(true);
    const uploadedUrl = await CloudService.uploadFile(file);
    let base64 = "";
    try {
        const reader = new FileReader();
        base64 = await new Promise((resolve) => {
            reader.onload = (ev) => resolve(ev.target?.result as string);
            reader.readAsDataURL(file);
        });
    } catch(e) { console.error("Base64 conversion failed", e); }
    const def = file.name.substring(0, 20).replace(/\.[^/.]+$/, "");
    const name = prompt("Enter sound name:", def);
    if (name === null) { setIsUploadingBallSound(false); return; }
    
    const soundId = Math.random().toString(36).substring(7);
    const soundName = name.trim() || def;

    if (uploadedUrl) {
      await CloudService.registerSound(soundName, uploadedUrl, soundId);
    }

    setConfig((prev: SimulationConfig) => ({ ...prev, userSounds: [...prev.userSounds, { 
        id: soundId, 
        name: soundName, 
        base64: base64, url: uploadedUrl || undefined 
    }] }));
    setIsUploadingBallSound(false);
  };

  return {
    handleFileUpload,
    handleBallSoundUpload,
    isAnalyzing,
    analysisProgress,
    isUploadingBallSound
  };
};
